import type { StateCreator } from 'zustand/vanilla';
import type { CreationInfo, TaskInfo } from '@/app/types';
import { isAudioTask, isImageTask } from '@/app/utils';

/**
 * 任务列表最后初始化的来源页面类型
 */
type TaskListInitSource = 'create' | 'template' | 'text2audio' | 'tts';

interface TaskListState {
  taskList: TaskInfo[];
  taskListInitSource?: TaskListInitSource; // 上次初始化任务列表的页面，如果没有值代表没有初始化过
  taskListQueryKey?: string;
  taskListUserId?: string;
  taskListNextPageToken?: string;
}

interface InitTaskListOptions {
  queryKey: string;
  userId?: string;
  nextPageToken?: string;
}

export interface TaskListStore extends TaskListState {
  /**
   * 初始化任务列表
   */
  initTaskList: (taskList: TaskInfo[], taskListInitSource: TaskListInitSource, options: InitTaskListOptions) => void;
  /**
   * 在任务列表的最前面添加一个新任务（通常用于用户创建新任务）
   * @param task 任务
   */
  addNewTask: (task: TaskInfo) => void;
  /**
   * 追加多个任务到列表末尾（通常用于分页加载更多任务）
   * @param taskList 多个任务
   */
  appendTaskList: (taskList: TaskInfo[], nextPageToken?: string) => void;
  /**
   * 删除任务
   * @param taskId 任务ID
   */
  removeTask: (taskId: string) => void;
  /**
   * 更新任务
   * @param taskId 任务ID
   * @param task 任务
   */
  updateTask: (taskId: string, task: Partial<TaskInfo>) => void;
  /**
   * 更新生成物生成物
   * @param taskId 任务ID
   * @param creationId 生成物ID
   * @param creation 生成物
   */
  updateCreation: (taskId: string, creationId: string, creation: Partial<CreationInfo>) => void;
  /**
   * 判断指定任务是否为首个成功的视频任务
   * @param taskId 任务ID
   */
  isFirstSuccessVideoTask: (taskId: string) => boolean;
  /**
   * 判断指定任务是否为首个成功的1080p视频任务
   * @param taskId 任务ID
   */
  isFirstSuccess1080pVideoTask: (taskId: string) => boolean;
  /**
   * 判断指定任务是否为首个成功的图片任务
   * @param taskId 任务ID
   */
  isFirstSuccessImageTask: (taskId: string) => boolean;
}

const initialState: TaskListState = {
  taskList: [],
};

export const createTaskListSlice: StateCreator<TaskListStore, [], [], TaskListStore> = (set, get, _store) => ({
  ...initialState,
  initTaskList: (taskList: TaskInfo[], taskListInitSource: TaskListInitSource, options: InitTaskListOptions) => {
    set({
      taskList,
      taskListInitSource,
      taskListQueryKey: options.queryKey,
      taskListUserId: options.userId,
      taskListNextPageToken: options.nextPageToken,
    });
  },
  addNewTask: (task: TaskInfo) => {
    set((state: TaskListStore) => ({
      taskList: [task, ...state.taskList],
    }));
  },
  appendTaskList: (taskList: TaskInfo[], nextPageToken?: string) => {
    set((state: TaskListStore) => ({
      taskList: state.taskList.concat(taskList),
      taskListNextPageToken: nextPageToken,
    }));
  },
  removeTask: (taskId: string) => {
    set((state: TaskListStore) => ({
      taskList: state.taskList.filter((task) => task.id !== taskId),
    }));
  },
  updateTask: (taskId: string, task: Partial<TaskInfo>) => {
    set((state: TaskListStore) => ({
      taskList: state.taskList.map((t) => (t.id === taskId ? { ...t, ...task } : t)),
    }));
  },
  updateCreation: (taskId: string, creationId: string, creation: Partial<CreationInfo>) => {
    set((state: TaskListStore) => ({
      taskList: state.taskList.map((task) =>
        task.id === taskId
          ? {
              ...task,
              creations: task.creations.map((c) => (c.id === creationId ? { ...c, ...creation } : c)),
            }
          : task,
      ),
    }));
  },
  isFirstSuccessVideoTask: (taskId: string) => {
    const state = get();
    const firstSuccessVideoTask = state.taskList.find(
      (task) => task.state === 'success' && !isAudioTask(task.type) && !isImageTask(task),
    );
    return firstSuccessVideoTask?.id === taskId;
  },
  isFirstSuccess1080pVideoTask: (taskId: string) => {
    const state = get();
    const firstSuccess1080pVideoTask = state.taskList.find(
      (task) =>
        task.state === 'success' &&
        !isAudioTask(task.type) &&
        !isImageTask(task) &&
        task.settings.resolution === '1080p',
    );
    return firstSuccess1080pVideoTask?.id === taskId;
  },
  isFirstSuccessImageTask: (taskId: string) => {
    const state = get();
    const firstSuccessImageTask = state.taskList.find((task) => task.state === 'success' && isImageTask(task));
    return firstSuccessImageTask?.id === taskId;
  },
});
